/**
 * Client-side utility for handling Seek authentication via new browser tab.
 * Uses a new tab instead of popup window to avoid popup blockers while maintaining
 * cross-window communication via postMessage.
 */

const TIMEOUT_S = 120;

export type SeekAuthResult =
  | {
      status: "success";
      seekToken: string | null;
      seekPrefillId: string | null;
    }
  | {
      status: "tab_blocked";
    }
  | {
      status: "user_cancelled";
    }
  | {
      status: "timeout";
    };

export class SeekAuthTab {
  private authWindow: Window | null = null;

  /**
   * Initiates Seek authentication flow and waits for completion.
   * Opens new tab, listens for auth completion, and handles all error cases.
   *
   * Error cases handled:
   * - Tab blocked: returns { status: "tab_blocked" }
   * - User closes tab: returns { status: "user_cancelled" }
   * - User clicks "no" / cancels auth: returns { status: "timeout" }
   * - Network errors: returns { status: "timeout" }
   * - Invalid response: returns { status: "timeout" }
   *
   * @returns Promise resolving to auth result or null if cancelled/timed out
   */
  public async authenticate(authUrl: string): Promise<{
    seekToken: string | null;
    seekPrefillId: string | null;
  } | null> {
    console.info("[SeekAuthTab] Starting authentication flow", { authUrl });
    // Step 1: Open new tab (much less likely to be blocked than popup window)
    // Using "_blank" target opens in new tab
    this.authWindow = window.open(authUrl, "_blank");

    if (!this.authWindow) {
      // window.open() returns null in these cases:
      // 1. The tab was blocked by the browser (popup blocker)
      // 2. The tab was opened but in a different Browsing Context Group
      //    (due to Cross-Origin-Opener-Policy headers), so we can't get a reference
      console.error("[SeekAuthTab] Failed to get reference to authentication window");
      throw new Error("Failed to open authentication window.");
    }

    console.info("[SeekAuthTab] Authentication tab opened successfully");

    // Step 2: Set up listener and wait for completion
    return new Promise((resolve) => {
      let resolved = false;
      let tabClosedCheckInterval: ReturnType<typeof setInterval> | null = null;

      const cleanupAndResolve = (
        value: {
          seekToken: string | null;
          seekPrefillId: string | null;
        } | null,
        reason?: string
      ) => {
        if (resolved) return;
        resolved = true;

        if (timeoutId) clearTimeout(timeoutId);
        if (tabClosedCheckInterval) clearInterval(tabClosedCheckInterval);
        window.removeEventListener("message", handleMessage);

        if (value) {
          console.info("[SeekAuthTab] Authentication flow completed", {
            reason: reason || "success",
            hasSeekToken: !!value.seekToken,
            hasSeekPrefillId: !!value.seekPrefillId,
          });
        } else {
          console.info("[SeekAuthTab] Authentication flow completed", {
            reason: reason || "cancelled or timed out",
          });
        }

        resolve(value);
      };

      const handleMessage = (event: MessageEvent) => {
        console.info("[SeekAuthTab] Received postMessage", {
          origin: event.origin,
          expectedOrigin: window.location.origin,
          dataType: typeof event.data,
          hasData: !!event.data,
        });

        // Verify message origin matches current window origin for security
        if (event.origin !== window.location.origin) {
          console.error("[SeekAuthTab] Message rejected - origin mismatch", {
            receivedOrigin: event.origin,
            expectedOrigin: window.location.origin,
          });
          return;
        }

        if (
          event.data &&
          typeof event.data === "object" &&
          event.data.type === "seekAuthComplete" &&
          (event.data.seekToken || event.data.seekPrefillId)
        ) {
          console.info("[SeekAuthTab] Valid authentication message received", {
            hasSeekToken: !!event.data.seekToken,
            hasSeekPrefillId: !!event.data.seekPrefillId,
          });
          cleanupAndResolve(
            {
              seekToken: event.data.seekToken || null,
              seekPrefillId: event.data.seekPrefillId || null,
            },
            "success"
          );
        } else {
          console.info(
            "[SeekAuthTab] Message ignored - invalid format or missing required fields",
            {
              hasData: !!event.data,
              dataType: event.data?.type,
              hasSeekToken: !!event.data?.seekToken,
              hasSeekPrefillId: !!event.data?.seekPrefillId,
            }
          );
        }
      };

      window.addEventListener("message", handleMessage);

      // Check if tab/window is closed manually (user closed tab)
      // Poll every 500ms - more responsive than waiting for timeout
      tabClosedCheckInterval = setInterval(() => {
        if (this.authWindow && this.authWindow.closed && !resolved) {
          // User closed tab manually - resolve with null
          console.info("[SeekAuthTab] Authentication tab was closed by user");
          cleanupAndResolve(null, "user_cancelled");
        }
      }, 500);

      // Timeout after 2 minutes - handles:
      // - User clicks "no" / cancels in Seek auth flow
      // - Network errors
      // - Invalid/empty responses
      // - Any other case where no message is received
      const timeoutId = setTimeout(() => {
        if (!resolved) {
          console.info("[SeekAuthTab] Authentication flow timed out after 2 minutes");
          cleanupAndResolve(null, "timeout");
        }
      }, TIMEOUT_S * 1000);
    });
  }
}

/**
 * Callback handler to be called by the authentication tab/window
 * When we receive the seek params (seek-token and seek-prefill-id) from the URL
 * we send a message to the opener window (the original job post page) and update the URL
 * with the seek params so the loader can fetch the seek profile
 */
export function handleSeekAuthCallback(): void {
  console.info("[SeekAuthCallback] Handling Seek authentication callback");

  if (typeof window === "undefined") {
    console.error("[SeekAuthCallback] Window is undefined - cannot proceed");
    return;
  }

  if (!window.opener) {
    console.error("[SeekAuthCallback] No window.opener found - cannot send message to parent");
    return;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const seekToken = urlParams.get("seek-token");
  const seekPrefillId = urlParams.get("seek-prefill-id");

  console.info("[SeekAuthCallback] Extracted URL parameters", {
    hasSeekToken: !!seekToken,
    hasSeekPrefillId: !!seekPrefillId,
    currentOrigin: window.location.origin,
  });

  // If we have Seek params, send message to opener
  if (seekToken || seekPrefillId) {
    try {
      const messageData = {
        type: "seekAuthComplete",
        seekToken,
        seekPrefillId,
      };

      console.info("[SeekAuthCallback] Sending postMessage to opener window", {
        targetOrigin: window.location.origin,
        hasSeekToken: !!seekToken,
        hasSeekPrefillId: !!seekPrefillId,
      });

      // Send message to opener with specific origin for security
      // Both callback page and parent page are on the same origin
      window.opener.postMessage(messageData, window.location.origin);

      console.info("[SeekAuthCallback] Successfully sent postMessage to opener");
      // Note: The callback page component handles auto-closing after 3 seconds
    } catch (error) {
      console.error("[SeekAuthCallback] Error communicating with opener window", {
        error: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined,
      });
    }
  } else {
    console.info("[SeekAuthCallback] No Seek parameters found in URL - nothing to send");
  }
}
